import { ref } from 'vue'
import { defineStore } from 'pinia'
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  onSnapshot,
  query,
  orderBy,
  serverTimestamp,
  type Unsubscribe,
} from 'firebase/firestore'
import { db } from '@/firebase'
import { writeAuditLog } from './auditLog'
import type { CommsChannel } from '@/types'

export const useCommsStore = defineStore('comms', () => {
  const channels = ref<CommsChannel[]>([])
  const isLoading = ref(false)
  let unsubFn: Unsubscribe | null = null
  let _incidentId = ''
  let _opId = ''

  function subscribe(incidentId: string, opId: string) {
    if (incidentId === _incidentId && opId === _opId && unsubFn) return
    if (unsubFn) unsubFn()
    _incidentId = incidentId
    _opId = opId
    isLoading.value = true
    const q = query(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'comms_plan'),
      orderBy('order'),
    )
    unsubFn = onSnapshot(q, (snap) => {
      channels.value = snap.docs.map((d) => ({ channel_id: d.id, ...d.data() }) as CommsChannel)
      isLoading.value = false
    })
  }

  function teardown() {
    if (unsubFn) {
      unsubFn()
      unsubFn = null
    }
    _incidentId = ''
    _opId = ''
    channels.value = []
    isLoading.value = false
  }

  async function addChannel(
    incidentId: string,
    opId: string,
    data: Pick<CommsChannel, 'name' | 'assignment'> &
      Partial<Pick<CommsChannel, 'rx_freq' | 'rx_tone' | 'tx_freq' | 'tx_tone' | 'mode' | 'remarks' | 'zone_group' | 'channel_number'>>,
  ) {
    const nextOrder = channels.value.length
    const docRef = await addDoc(
      collection(db, 'incidents', incidentId, 'operational_periods', opId, 'comms_plan'),
      {
        name: data.name,
        assignment: data.assignment,
        rx_freq: data.rx_freq ?? '',
        rx_tone: data.rx_tone ?? '',
        tx_freq: data.tx_freq ?? '',
        tx_tone: data.tx_tone ?? '',
        mode: data.mode ?? 'A',
        remarks: data.remarks ?? '',
        zone_group: data.zone_group ?? '',
        channel_number: data.channel_number ?? '',
        order: nextOrder,
        created_at: serverTimestamp(),
      },
    )
    await writeAuditLog(incidentId, 'op.comms_channel_added', 'comms_plan', docRef.id, null, {
      name: data.name,
      assignment: data.assignment,
    })
  }

  async function updateChannel(
    incidentId: string,
    opId: string,
    channelId: string,
    updates: Partial<Pick<CommsChannel, 'name' | 'assignment' | 'rx_freq' | 'rx_tone' | 'tx_freq' | 'tx_tone' | 'mode' | 'remarks' | 'zone_group' | 'channel_number'>>,
  ) {
    const before = channels.value.find((c) => c.channel_id === channelId) ?? null
    await updateDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'comms_plan', channelId),
      updates,
    )
    await writeAuditLog(
      incidentId,
      'op.comms_channel_updated',
      'comms_plan',
      channelId,
      before as Record<string, unknown> | null,
      updates,
    )
  }

  async function removeChannel(incidentId: string, opId: string, channelId: string) {
    const before = channels.value.find((c) => c.channel_id === channelId) ?? null
    await deleteDoc(
      doc(db, 'incidents', incidentId, 'operational_periods', opId, 'comms_plan', channelId),
    )
    await writeAuditLog(
      incidentId,
      'op.comms_channel_removed',
      'comms_plan',
      channelId,
      before as Record<string, unknown> | null,
      null,
    )
  }

  return { channels, isLoading, subscribe, teardown, addChannel, updateChannel, removeChannel }
})
